import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(
        data.status === "already_subscribed"
          ? "You're already on the reading list."
          : "Welcome to the 1123 Reading Room."
      );
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-navy text-ivory mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="1123 Publishing Company Logo" className="h-14 w-14 border border-gold/40" />
            <div>
              <div className="font-serif text-2xl">1123 Publishing Company</div>
              <div className="overline text-gold">Unfiltered Narratives. Restored Legacies.</div>
            </div>
          </div>
          <p className="mt-6 text-ivory/75 leading-relaxed max-w-md">
            An independent publishing house bringing high-impact memoir, social-justice narrative,
            and transformational literature to the global stage.
          </p>
        </div>

        <div className="lg:col-span-3 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="overline mb-4 text-gold">House</div>
            <ul className="space-y-2">
              <li><Link to="/bookstore" className="hover:text-gold">Bookstore</Link></li>
              <li><Link to="/authors" className="hover:text-gold">Authors</Link></li>
              <li><Link to="/services" className="hover:text-gold">Publishing</Link></li>
              <li><Link to="/submit" className="hover:text-gold">Submit Manuscript</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-4 text-gold">Mission</div>
            <ul className="space-y-2">
              <li><Link to="/foundation" className="hover:text-gold">Foundation</Link></li>
              <li><Link to="/tcpi" className="hover:text-gold">TCPI</Link></li>
              <li>
                <a
                  href="https://thecrownpathinitiative.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gold inline-flex items-center gap-1.5"
                  data-testid="footer-tcpi-site"
                >
                  TCPI.org
                  <span aria-hidden="true" className="text-[10px]">&#8599;</span>
                </a>
              </li>
              <li><Link to="/academy" className="hover:text-gold">Academy</Link></li>
              <li><Link to="/media" className="hover:text-gold">Media</Link></li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="overline mb-4 text-gold">The Reading Room</div>
          <p className="text-ivory/75 text-sm leading-relaxed mb-4">
            New releases, author interviews, and editor letters. No spam. Cancel any time.
          </p>
          <form onSubmit={subscribe} className="flex border border-ivory/30">
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:outline-none"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-gold text-navy px-5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:opacity-80 disabled:opacity-50"
              data-testid="newsletter-submit"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-ivory/15">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-ivory/55">
          <div>
            <Link
              to="/admin/login"
              aria-label="Editorial desk"
              data-testid="footer-admin-hidden"
              className="cursor-default select-none no-underline text-ivory/55 hover:text-ivory/55"
              style={{ textDecoration: "none" }}
            >
              &copy;
            </Link>
            {" "}{new Date().getFullYear()} 1123 Publishing Company. All rights reserved.
          </div>
          <div className="flex gap-6 uppercase tracking-[0.2em]">
            <Link to="/foundation" className="hover:text-gold">Donate</Link>
            <span className="hover:text-gold cursor-pointer">Privacy</span>
            <span className="hover:text-gold cursor-pointer">Terms</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
